import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { cx, isFilled } from '@/shared/lib'
import { CounterBadge } from '@/shared/ui/CounterBadge'
import s from './AppNav.module.css'

/** Служебные пропсы пункта для слота `render`: раскладываются на свой элемент как есть. */
export type AppNavItemSlotProps = {
  className: string
  children: ReactNode
  'aria-current': 'page' | undefined
  'aria-label': string | undefined
  'data-nav-item': string
}

export type AppNavItem = {
  /** Ключ отрисовки и якорь теста (`data-nav-item`). */
  id: string
  /** Подпись раздела строкой: ниже 480px она уходит в доступное имя, на экране остаётся глиф. */
  label: string
  /** Глиф. Обязателен: на узком экране он единственное, что от пункта остаётся. */
  icon: ReactNode
  /** Счётчик раздела. Приходит с бэка готовым, компонент его не считает. Ноль не рисуется. */
  badge?: number
  /** Фраза вместо голых цифр: «4 позиции в корзине». Уходит в доступное имя пункта. */
  badgeLabel?: string
  /** Раздел, в котором пользователь сейчас находится. */
  current?: boolean
  /** Пункт-переход: `<a href>` со средним кликом и «открыть в новой вкладке». */
  href?: string
  /** Пункт-переход роутера: элемент создаёт потребитель — компонент о роутере не знает. */
  render?: (props: AppNavItemSlotProps) => ReactNode
}

export type AppNavProps = {
  /** Разделы в порядке отрисовки. Ровно то, что рисуем: ни сортировки, ни фильтрации по правам. */
  items: AppNavItem[]
  /** Доступное имя `<nav>`: «Разделы системы». */
  label: string
  /** Хвост строки, прижатый к правому краю шапки: кнопка монитора процессов. */
  trailing?: ReactNode
  ref?: Ref<HTMLElement>
} & Omit<HTMLAttributes<HTMLElement>, 'children'>

/**
 * Горизонтальное меню кабинета во второй строке шапки: капсулы разделов с глифом и подписью,
 * текущий раздел — акцентной заливкой. Заменяет боковую колонку: главная и остальные экраны
 * живут во всю ширину.
 *
 * Mobile first: капсулы переносятся; ниже 480px подпись скрыта визуально, пункт — тач-цель
 * 44px с одним глифом, имя остаётся у ссылки. Хвост строки (`trailing`) прижат к правому краю
 * и переносится целиком.
 */
export function AppNav({ items, label, trailing, className, ref, ...rest }: AppNavProps) {
  return (
    <nav {...rest} ref={ref} className={cx(s.root, className)} aria-label={label}>
      <ul className={s.list}>
        {items.map((item) => {
          const badge = item.badge !== undefined && item.badge > 0 ? item.badge : undefined
          const name =
            badge !== undefined && item.badgeLabel ? `${item.label}, ${item.badgeLabel}` : undefined
          const content = (
            <>
              <span className={s.icon} aria-hidden="true">
                {item.icon}
              </span>
              <span className={s.label}>{item.label}</span>
              {badge !== undefined ? (
                <CounterBadge
                  className={s.badge}
                  value={badge}
                  size="xs"
                  tone={item.current ? 'onAccent' : 'accentTint'}
                  decorative
                />
              ) : null}
            </>
          )
          const slotProps: AppNavItemSlotProps = {
            className: cx(s.item, item.current && s.current),
            children: content,
            'aria-current': item.current ? 'page' : undefined,
            'aria-label': name,
            'data-nav-item': item.id,
          }

          return (
            <li key={item.id} className={s.cell}>
              {item.render !== undefined ? (
                item.render(slotProps)
              ) : (
                <a {...slotProps} href={item.href}>
                  {content}
                </a>
              )}
            </li>
          )
        })}
      </ul>

      {isFilled(trailing) ? <div className={s.trailing}>{trailing}</div> : null}
    </nav>
  )
}
